import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { AppealsPacketFormData } from '../models/appeals-packet.model';
import { ShellService } from '@mynexus/mfe-core';
import { ProductNames } from '@mynexus/mynexus-ui-lib';
import { MemberSearch } from '@app/core/model/core-member-model';
import { IDocumentResponseDTO } from '@core/model';
import { DocumentSource } from '@app/core/enumerations';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppealsPacketService {
  private readonly http = inject(HttpClient);
  private readonly shellService = inject(ShellService);
  private readonly PAC_API_URL = this.shellService.getBaseUrlFor(ProductNames.PACIM);
  private readonly HH_API_URL = this.shellService.getBaseUrlFor(ProductNames.HomeHealth);

  generateAppealsPacket(payLoad: any, authorizationId: string): Observable<string> {
    const tenantId = this.shellService.getTenantId();

    const apiUrl = `${this.PAC_API_URL}/tenants/${tenantId}/authorizations/${authorizationId}/documents/CreateAppealPacket`;
    return this.http.post(apiUrl, payLoad, { responseType: 'text' });
  }

  getNotesByAuthorizationNumber(authorizationNumber: string, noteTypesConfig: any[]): Observable<any> {
    const tenantId = this.shellService.getTenantId();
    const url = `${this.PAC_API_URL}/${tenantId}/notes/authorization-number/${authorizationNumber}`;

    return this.http.post<any>(url, noteTypesConfig).pipe(
      tap(notes => {
      }),
      catchError(error => {
        console.error('[AppealsPacketService] Error fetching notes:', error);
        return of([]);
      })
    );
  }

  getMedicalHistory(authorizationByProductId: string): Observable<any> {
    const tenantId = this.shellService.getTenantId();
    const url = `${this.PAC_API_URL}/tenant/${tenantId}/authorizations/${authorizationByProductId}/MedicalHistory`;

    return this.http.get<any[]>(url).pipe(
      map(medicalHistoryArray => {
        if (medicalHistoryArray && medicalHistoryArray.length > 0) {
          // Return the first item's historyOfPresentIllness
          return medicalHistoryArray[0].historyOfPresentIllness || '';
        }
        return '';
      }),
      catchError(error => {
        console.error('[AppealsPacketService] Error fetching medical history:', error);
        return of('');
      })
    );
  }

  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  }

  getAuthAppealDetails(authorizationNumber: string, packetType: string): Observable<any> {
    const tenantId = this.shellService.getTenantId();
    const authUrl = `${this.PAC_API_URL}/tenants/${tenantId}/Authorizations/${authorizationNumber}`;    
    return this.http.get<any>(authUrl).pipe(
      switchMap(authorization => {

        const memberId = authorization?.authorizationProduct?.memberId;
        if (!memberId) {
          return of({
            ...authorization,
            memberData: null,
            mbi: '—'
          });
        }
        
        const memberUrl = `${this.HH_API_URL}/Search/SearchMemberById/${tenantId}/${memberId}`;
        
        return this.http.get<MemberSearch>(memberUrl).pipe(
          map(memberSearch => {

            const member = memberSearch?.memberDTO;
            const mbiValue = member?.mbiNumber;

            const formattedMember = {
              ...member,
              dateOfBirth: this.formatDate(member?.dateOfBirth),
              eligibilityEffectiveBeginDate: this.formatDate(member?.eligibilityEffectiveBeginDate),
              eligibilityEffectiveEndDate: this.formatDate(member?.eligibilityEffectiveEndDate),
              mbiNumber: mbiValue
            };
            
            return {
              ...authorization,
              memberData: formattedMember,
              dateOfBirth: this.formatDate(member?.dateOfBirth),
              mbi: mbiValue
            };
          }),
          catchError(error => {
            console.error('[AppealsPacketService] Error fetching member data:', error);
            return of({
              ...authorization,
              memberData: null,
              mbi: '--'
            });
          })
        );
      })
    );
  }

  getAppealsAndGrievances(tenantId: string, queryParams: string, isExternal = false): Observable<{items: any[], count: number}> {
    const appealandGrievance = isExternal ? 'AppealAndGrievanceExternal' : 'AppealsAndGrievances';
    const url = `${this.HH_API_URL}/tenants/${tenantId}/${appealandGrievance}${queryParams}`;
        
    return this.http.get<any>(url).pipe(
      map(response => ({
        items: response.value || response.items || [],
        count: response['@odata.count'] || response.count || 0
      })),
      catchError(error => {
        console.error('[AppealsPacketService] Error fetching Appeals and Grievances:', error);
        return of({ items: [], count: 0 });
      })
    );
  }

  getAppealsAndGrievancesData(appealsAndGrievancesId: string, isExternal = false): Observable<any> {
    const tenantId = this.shellService.getTenantId();
    const appealandGrievance = isExternal ? 'AppealAndGrievanceExternal' : 'AppealsAndGrievances';
    const url = `${this.HH_API_URL}/tenants/${tenantId}/${appealandGrievance}/${appealsAndGrievancesId}`;
    return this.http.get<any>(url).pipe(
      tap(data => {

      }),
      catchError(error => {
        console.error('[AppealsPacketService] Error fetching Appeals and Grievances data:', error);
        return of(null);
      })
    );
  }

  getAppealAndGrievanceNotification(appealAndGrievanceId: string, isExternal = false): Observable<any> {
    const tenantId = this.shellService.getTenantId();
    const url = isExternal
      ? `${this.HH_API_URL}/tenants/${tenantId}/AppealAndGrievanceExternal/${appealAndGrievanceId}/GetAppealAndGrievanceNotificationExternal`
      : `${this.HH_API_URL}/tenants/${tenantId}/AppealsAndGrievances/${appealAndGrievanceId}/AppealAndGrievanceNotification/GetappealAndGrievanceNotification`;
    return this.http.get<any>(url).pipe(
      catchError(error => {
        console.error('[AppealsPacketService] Error fetching Appeal And Grievance Notification:', error);
        return of(null);
      })
    );
  }

  getAppealsPacketReferences(): Observable<{exhibits: string[], denialReasons: string[]}> {
    const url = `${this.PAC_API_URL}/AppealsPacket`;
    return this.http.get<{exhibits: string[], primaryDenialReasons: string[]}>(url).pipe(
      map(config => ({
        exhibits: config.exhibits || [],
        denialReasons: config.primaryDenialReasons || []
      })),
      catchError(error => {
        console.error('Error fetching configuration:', error);
        return of({ exhibits: [], denialReasons: [] });
      })
    );
  }

  getAuthNotesByTypes(authorizationNumber: string, noteTypesConfigList: any[]): Observable<any> {
    const tenantId = this.shellService.getTenantId();
    return this.http.post<any>(
      `${this.PAC_API_URL}/${tenantId}/notes/authorization-number/${authorizationNumber}`,
      noteTypesConfigList
    );
  }

   getByAuthorization(tenantId: string, authorizationNumber: string): Observable<any> {
    const url = `${this.PAC_API_URL}/tenants/${tenantId}/authorizations/${authorizationNumber}/Facilities`;
    return this.http.get<any>(url);
  }

  filterDocuments(documents: IDocumentResponseDTO[]): IDocumentResponseDTO[] {
    return (documents || []).filter(doc => doc.documentSource !== DocumentSource.Appeals);
  }
}
